import { css } from "@emotion/react";
import { Suspense } from "react";
import { usePreloadedQuery } from "react-relay";
import { useLoaderData } from "react-router";
import invariant from "tiny-invariant";

import { DebouncedSearch, LinkButton, Loading } from "@phoenix/components";
import { CanModify } from "@phoenix/components/auth";
import { useEvaluatorsFilterContext } from "@phoenix/pages/evaluators/EvaluatorsFilterProvider";
import { EvaluatorsFilterProvider } from "@phoenix/pages/evaluators/EvaluatorsFilterProvider";
import type { EvaluatorsPageLoaderType } from "@phoenix/pages/evaluators/evaluatorsPageLoader";
import { evaluatorsPageLoaderGql } from "@phoenix/pages/evaluators/evaluatorsPageLoader";
import { GlobalEvaluatorsTable } from "@phoenix/pages/evaluators/GlobalEvaluatorsTable";

const pageWrapperCSS = css`
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  font-family: var(--ac-premium-font-family, 'Century Gothic', sans-serif);
`;

const headerContainerCSS = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: -0.02em;
    color: var(--global-text-color-900, #191c1d);
    margin: 0 0 6px 0;
  }

  p {
    font-size: 13px;
    color: var(--global-color-gray-600, #575e70);
    margin: 0;
  }
`;

const filterBarCSS = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  input, [role="searchbox"] {
    border-radius: 9999px !important;
    padding-left: 36px !important;
    background-color: var(--global-color-surface-container-lowest, #ffffff) !important;
    border: 1px solid var(--global-color-outline-variant, #bcc9c6) !important;
  }

  .filter-select {
    background-color: var(--global-color-surface-lowest, #ffffff);
    border: 1px solid var(--global-color-outline-variant, #bcc9c6);
    border-radius: 9999px;
    font-size: 14px;
    padding: 8px 32px 8px 16px;
    appearance: none;
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 10px;
    color: var(--global-text-color-900, #191c1d);
    cursor: pointer;
    font-family: var(--ac-premium-font-family, 'Century Gothic', sans-serif);

    &:focus {
      outline: none;
      border-color: var(--ac-premium-teal, #00685f);
    }
  }
`;

export const EvaluatorsPage = () => {
  const loaderData = useLoaderData<EvaluatorsPageLoaderType>();
  invariant(loaderData, "loaderData is required");
  const query = usePreloadedQuery(evaluatorsPageLoaderGql, loaderData);
  return (
    <EvaluatorsFilterProvider>
      <EvaluatorsPageContent query={query} />
    </EvaluatorsFilterProvider>
  );
};

const EvaluatorsPageContent = ({ query }: { query: any }) => {
  const { filter, setFilter } = useEvaluatorsFilterContext();

  return (
    <div css={pageWrapperCSS}>
      {/* Page Header */}
      <div css={headerContainerCSS}>
        <div>
          <h2>Evaluators</h2>
          <p>Manage and test your evaluation metrics</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div css={filterBarCSS}>
        <div style={{ flex: "1 1 100%", width: "100%" }}>
          <DebouncedSearch
            aria-label="Search evaluators by name"
            onChange={setFilter}
            defaultValue={filter}
            placeholder="Search evaluators by name..."
          />
        </div>
      </div>

      {/* Table Content */}
      <Suspense fallback={<Loading />}>
        <GlobalEvaluatorsTable query={query} />
      </Suspense>
    </div>
  );
};
